export interface GenerationForecastWindow {
  fromTimestamp: string;
  toTimestamp: string;
  fromLocal: string;
  toLocal: string;
  fromDate: string;
  toDate: string;
}

export interface GenerationForecastSummary {
  rows: number;
  totalForecastEnergyKwh: number;
  maxHourlyKwh: number;
  firstForecastForLocal: string;
  lastForecastForLocal: string;
}

export interface GenerationForecastDaily {
  day: string;
  rows: number;
  missingRows?: number;
  forecastEnergyKwh: number;
  maxHourlyKwh: number;
  avgCloudCover: number;
  maxShortwaveRadiation: number;
  minTemperature2m: number;
  maxTemperature2m: number;
}

export interface GenerationForecastLatestResponse {
  modelVersion: string;
  forecastRunAt: string;
  forecastWindow: GenerationForecastWindow | null;
  summary: GenerationForecastSummary | null;
  daily: GenerationForecastDaily[];
}

export interface GenerationForecastDayHourly {
  hour: string;
  forecastEnergyKwh: number | null;
  forecastPowerKw: number | null;
  weather: {
    temperature2m: number | null;
    cloudCover: number | null;
    windSpeed10m: number | null;
    precipitation: number | null;
    shortwaveRadiation: number | null;
    directRadiation: number | null;
    diffuseRadiation: number | null;
    directNormalIrradiance: number | null;
  };
  colorHint?: 'gray' | 'red' | 'muted';
  status: string;
}

export interface GenerationForecastDaySun {
  sunrise: string;
  sunset: string;
}

export interface GenerationForecastDaySummary {
  rows: number;
  missingRows: number;
  availableRows: number;
  forecastEnergyKwh: number;
  maxHourlyKwh: number;
  avgCloudCover: number;
  maxShortwaveRadiation: number;
  minTemperature2m: number;
  maxTemperature2m: number;
}

export interface GenerationForecastDayResponse {
  algorithmRevision?: string | null;
  date: string;
  modelVersion: string;
  targetType: string;
  inputWeatherProvider: string;
  inputWeatherRunAt: string;
  forecastRunAt: string;
  summary: GenerationForecastDaySummary;
  hourly: GenerationForecastDayHourly[];
  sun?: GenerationForecastDaySun;
}

export interface PvActualHourly {
  hour: string;
  actualPacKw: number | null;
  actual7Kw: number | null;
  unit6Kw: number | null;
  coverage: number | null;
  qualityFlag: string | null;
  readAtLocal: string | null;
  samplePosition: string | null;
  status: string;
  isFinal: boolean;
}

export interface PvActualDayResponse {
  date: string;
  totalActualKwhApprox: number | null;
  actual: PvActualHourly[];
}

// ─── Half-Hour Forecast Comparison API types ──────────────────────────────
export interface ForecastHalfHourPoint {
  time: string;
  forecastSlot: string;
  sampleTargetLocal: string;
  baseForecastKw: number | null;
  baseForecastKwClamped?: number | null;
  curtailmentCapKw?: number | null;
  baseForecastKwh: number | null;
  actualPacKw: number | null;
  actualKwhApprox: number | null;
  actualPvEquivalentKw: number | null;
  deltaBaseKw: number | null;
  deltaBasePct: number | null;
  baseAbsErrorKw: number | null;
  bessChargeKw: number | null;
  bessDischargeKw: number | null;
  bessPowerKw: number | null;
  socPct: number | null;
  calibrationMode: string | null;
  calibrationWeight: number | null;
  shortwaveRadiation: number | null;
  directRadiation: number | null;
  diffuseRadiation: number | null;
  cloudCover: number | null;
  temperature2m: number | null;
  weatherInterpolation: string | null;
  coverage: number | null;
  qualityFlag: string | null;
  bessQualityFlag: string | null;
  deltaSeverity: string | null;
}

export interface ForecastHalfHourSummary {
  forecastKwh: number | null;
  actualKwh: number | null;
  absErrorKwh: number | null;
  rowsWithForecast: number | null;
  rowsWithActual: number | null;
  interval: string;
  // New fields from improved backend endpoint
  actualKwhApprox?: number | null;
  baseForecastKwhApprox?: number | null;
}

export interface ForecastHalfHourResponse {
  date: string;
  interval: string;
  rows: number;
  summary: ForecastHalfHourSummary | null;
  points: ForecastHalfHourPoint[];
  activeCurtailment?: {
    id: string;
    startedAtLocal: string;
    endedAtLocal: string | null;
    curtailmentPct: number | null;
    curtailmentPmaxKw: number;
    source: string;
    reason: string | null;
  } | null;
}

// ─── Forecast Comparison API types ─────────────────────────────────────────
export interface ForecastComparisonPoint {
  hour: string;
  forecastHour?: string | null;
  baseForecastKw: number | null;
  actualPacKw: number | null;
  v034ShadowForecastKw: number | null;
  v038ShadowForecastKw: number | null;
  deltaBaseKw: number | null;
  deltaBasePct: number | null;
  deltaV034Kw: number | null;
  deltaV034Pct: number | null;
  deltaV038Kw: number | null;
  deltaV038Pct: number | null;
  v038AbsErrorKw: number | null;
  calibrationMode: string | null;
  bessChargeKw: number | null;
  bessDischargeKw: number | null;
  socPct: number | null;
  cloudCover: number | null;
  shortwaveRadiation: number | null;
  directRadiation: number | null;
  ecmwfShortwaveRadiation: number | null;
  ecmwfDirectRadiation: number | null;
  weatherComparisonSignal: string | null;
  v034CorrectionReason: string | null;
  v038CorrectionReason: string | null;
  v045HybridForecastKw: number | null;
  v045HybridSelectedSourceModel: string | null;
  v045HybridCorrectionReason: string | null;
  v045HybridMiddayRegime: string | null;
}

export interface ForecastComparisonSummary {
  baseForecastKwh: number | null;
  actualKwh: number | null;
  v034ShadowKwh: number | null;
  v038ShadowKwh: number | null;
  baseAbsErrorKwh: number | null;
  v034AbsErrorKwh: number | null;
  v038AbsErrorKwh: number | null;
  improvementVsBaseKwh: number | null;
  improvementV038VsBaseKwh: number | null;
  // Approx fields for current-day partial data
  v038ShadowKwhApprox: number | null;
  v038AbsErrorKwhApprox: number | null;
  v038ImprovementVsBaseKwhApprox: number | null;
  v038ImprovementVsV034KwhApprox: number | null;
}

export interface ForecastComparisonResponse {
  algorithmRevision?: string | null;
  date: string;
  assetId: string;
  points: ForecastComparisonPoint[];
  summary: ForecastComparisonSummary | null;
}

function formatDateLocal(d: Date): string {
  return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatTimeLocal(d: Date): string {
  return d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
}
function fmtDateShort(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function shiftDate(base: Date, days: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return d;
}

// ─── Rolling date helpers ───────────────────────────────────────────────────
const today = new Date();
export const kyivToday = fmtDateShort(today);
export const kyivTodayDisplay = formatDateLocal(today);

export function getDayTabLabels(): { label: string; apiDate: string; displayDate: string }[] {
  const t = new Date(today);
  return [
    { label: 'D-2', offset: -2 },
    { label: 'D-1', offset: -1 },
    { label: 'Today', offset: 0 },
    { label: 'D+1', offset: 1 },
    { label: 'D+2', offset: 2 },
  ].map((item) => {
    const d = shiftDate(t, item.offset);
    return {
      label: item.label,
      apiDate: fmtDateShort(d),
      displayDate: formatDateLocal(d),
    };
  });
}

// ─── Weather curves by day ────────────────────────────────────────────────────
const typicalForecast = [
  0, 0, 0, 0, 0, 0.18, 0.72, 1.48, 2.34, 3.10, 3.82, 4.15, 4.42, 4.50, 4.28, 3.91, 3.20, 2.38, 1.54, 0.78, 0.22, 0.04, 0, 0,
];
const tomorrowForecast = [
  0, 0, 0, 0, 0, 0.15, 0.65, 1.40, 2.20, 2.90, 3.60, 3.95, 4.20, 4.28, 4.05, 3.70, 3.00, 2.20, 1.40, 0.70, 0.18, 0.03, 0, 0,
];
const d2Forecast = [
  0, 0, 0, 0, 0, 0.12, 0.55, 1.28, 2.05, 2.75, 3.42, 3.80, 4.02, 4.10, 3.88, 3.52, 2.85, 2.08, 1.30, 0.62, 0.15, 0.02, 0, 0,
];
const yesterdayForecast = [
  0, 0, 0, 0, 0, 0.20, 0.80, 1.62, 2.50, 3.30, 4.01, 4.38, 4.60, 4.65, 4.40, 4.02, 3.30, 2.45, 1.60, 0.82, 0.25, 0.05, 0, 0,
];
const dMinus2Forecast = [
  0, 0, 0, 0, 0, 0.10, 0.50, 1.20, 2.00, 2.80, 3.50, 3.90, 4.10, 4.20, 4.00, 3.60, 2.90, 2.10, 1.30, 0.60, 0.15, 0.02, 0, 0,
];

const cloudsByDay: Record<string, number[]> = {
  [fmtDateShort(shiftDate(today, -2))]: [35,35,32,30,28,28,32,38,42,40,35,30,32,28,30,35,40,42,38,32,30,28,30,35],
  [fmtDateShort(shiftDate(today, -1))]: [10,10,8,8,8,10,12,15,18,16,12,10,12,10,12,15,18,22,20,15,12,10,12,15],
  [fmtDateShort(shiftDate(today, 0))]: [20,20,18,16,15,15,20,25,30,28,22,18,20,15,18,22,28,30,25,20,18,15,18,20],
  [fmtDateShort(shiftDate(today, 1))]: [30,30,28,28,25,25,30,35,40,38,32,28,30,25,28,32,38,40,35,30,28,25,28,30],
  [fmtDateShort(shiftDate(today, 2))]: [35,35,32,30,28,28,32,38,42,40,35,30,32,28,30,35,40,42,38,32,30,28,30,35],
};

const tempsByDay: Record<string, number[]> = {
  [fmtDateShort(shiftDate(today, -2))]: [10,9,9,8,8,9,11,13,16,18,20,21,22,22,21,20,18,16,15,14,12,11,10,10],
  [fmtDateShort(shiftDate(today, -1))]: [13,12,11,10,10,12,14,16,19,21,23,24,25,25,24,23,21,19,18,16,15,14,13,13],
  [fmtDateShort(shiftDate(today, 0))]: [12,11,11,10,10,11,13,15,18,20,22,23,24,24,23,22,20,18,17,16,14,13,12,12],
  [fmtDateShort(shiftDate(today, 1))]: [11,10,10,9,9,10,12,14,17,19,21,22,23,23,22,21,19,17,16,15,13,12,11,11],
  [fmtDateShort(shiftDate(today, 2))]: [10,9,9,8,8,9,11,13,16,18,20,21,22,22,21,20,18,16,15,14,12,11,10,10],
};

const windByDay: Record<string, number[]> = {
  [fmtDateShort(shiftDate(today, -2))]: [2,2,2,2,2,2,3,3,4,4,4,3,3,3,3,3,3,3,2,2,2,2,2,2],
  [fmtDateShort(shiftDate(today, -1))]: [3,3,2,2,2,3,3,4,5,5,4,4,3,3,3,3,3,4,4,3,3,3,3,3],
  [fmtDateShort(shiftDate(today, 0))]: [2,2,2,2,2,2,3,3,4,4,5,5,4,4,4,3,3,3,3,3,2,2,2,2],
  [fmtDateShort(shiftDate(today, 1))]: [3,3,3,2,2,3,3,4,5,5,5,4,4,4,3,3,3,3,3,3,3,3,3,3],
  [fmtDateShort(shiftDate(today, 2))]: [2,2,2,2,2,2,3,3,4,4,4,4,4,3,3,3,3,3,2,2,2,2,2,2],
};

const precipByDay: Record<string, number[]> = {
  [fmtDateShort(shiftDate(today, -2))]: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  [fmtDateShort(shiftDate(today, -1))]: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  [fmtDateShort(shiftDate(today, 0))]: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  [fmtDateShort(shiftDate(today, 1))]: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  [fmtDateShort(shiftDate(today, 2))]: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
};

function buildHourlyData(dateStr: string, forecastCurve: number[]): GenerationForecastDayHourly[] {
  const clouds = cloudsByDay[dateStr] ?? Array(24).fill(20);
  const temps = tempsByDay[dateStr] ?? Array(24).fill(15);
  const wind = windByDay[dateStr] ?? Array(24).fill(3);
  const precip = precipByDay[dateStr] ?? Array(24).fill(0);
  return forecastCurve.map((f, i) => {
    const shortwave = Math.round((f / Math.max(4.5, 0.01)) * 920);
    const direct = Math.round(shortwave * 0.55);
    const diffuse = Math.round(shortwave * 0.35);
    const dni = Math.round(shortwave * 0.65);
    return {
      hour: `${String(i).padStart(2, '0')}:00`,
      forecastEnergyKwh: f,
      forecastPowerKw: f > 0 ? Math.round(f * 1000) : 0,
      weather: {
        temperature2m: temps[i] ?? 15,
        cloudCover: clouds[i] ?? 20,
        windSpeed10m: wind[i] ?? 3,
        precipitation: precip[i] ?? 0,
        shortwaveRadiation: shortwave,
        directRadiation: direct,
        diffuseRadiation: diffuse,
        directNormalIrradiance: dni,
      },
      status: f > 0 ? 'ok' : 'ok',
    };
  });
}

function makeDaySummary(hourly: GenerationForecastDayHourly[]): GenerationForecastDaySummary {
  const valid = hourly.filter((h) => h.forecastEnergyKwh != null);
  const energy = parseFloat(valid.reduce((s, h) => s + (h.forecastEnergyKwh ?? 0), 0).toFixed(1));
  const maxHr = valid.length > 0 ? Math.max(...valid.map((h) => h.forecastEnergyKwh ?? 0)) : 0;
  const avgCloud = valid.length > 0 ? parseFloat((valid.reduce((s, h) => s + (h.weather.cloudCover ?? 0), 0) / valid.length).toFixed(1)) : 0;
  const maxRad = valid.length > 0 ? Math.max(...valid.map((h) => h.weather.shortwaveRadiation ?? 0)) : 0;
  const temps = valid.map((h) => h.weather.temperature2m ?? 0).filter((t) => t !== 0);
  const minTemp = temps.length > 0 ? Math.min(...temps) : 0;
  const maxTemp = temps.length > 0 ? Math.max(...temps) : 0;
  return {
    rows: 24,
    missingRows: 0,
    availableRows: 24,
    forecastEnergyKwh: energy,
    maxHourlyKwh: parseFloat(maxHr.toFixed(3)),
    avgCloudCover: avgCloud,
    maxShortwaveRadiation: maxRad,
    minTemperature2m: minTemp,
    maxTemperature2m: maxTemp,
  };
}

function makeSun(dateStr: string): GenerationForecastDaySun {
  // sunrise/sunset approximations for Ukraine in spring
  return {
    sunrise: `${dateStr}T06:15:00Z`,
    sunset: `${dateStr}T19:45:00Z`,
  };
}

// ─── PV actual mock curves (realistic measured data, slightly different from forecast) ───
const typicalActual = [
  0, 0, 0, 0, 0, 0.15, 0.68, 1.42, 2.28, 3.05, 3.75, 4.10, 4.35, 4.45, 4.20, 3.85, 3.15, 2.32, 1.48, 0.72, 0.18, 0.03, 0, 0,
];
const tomorrowActual = [
  0, 0, 0, 0, 0, 0.12, 0.60, 1.35, 2.15, 2.85, 3.55, 3.90, 4.15, 4.22, 3.98, 3.65, 2.95, 2.15, 1.35, 0.65, 0.15, 0.02, 0, 0,
];
const d2Actual = [
  0, 0, 0, 0, 0, 0.10, 0.50, 1.22, 1.98, 2.68, 3.35, 3.72, 3.95, 4.02, 3.82, 3.45, 2.80, 2.02, 1.25, 0.58, 0.12, 0.01, 0, 0,
];
const yesterdayActual = [
  0, 0, 0, 0, 0, 0.22, 0.85, 1.68, 2.58, 3.38, 4.08, 4.42, 4.65, 4.70, 4.45, 4.08, 3.35, 2.48, 1.62, 0.85, 0.22, 0.04, 0, 0,
];
const dMinus2Actual = [
  0, 0, 0, 0, 0, 0.08, 0.45, 1.15, 1.92, 2.72, 3.42, 3.82, 4.02, 4.12, 3.92, 3.52, 2.82, 2.05, 1.25, 0.55, 0.12, 0.01, 0, 0,
];

function buildPvActualData(dateStr: string, actualCurve: number[], isFutureDay: boolean = false, currentHour?: number): PvActualHourly[] {
  return actualCurve.map((val, i) => {
    if (isFutureDay) {
      return {
        hour: `${String(i).padStart(2, '0')}:00`,
        actualPacKw: null,
        actual7Kw: null,
        unit6Kw: null,
        coverage: null,
        qualityFlag: null,
        readAtLocal: null,
        samplePosition: null,
        status: 'future',
        isFinal: false,
      };
    }
    const isCurrent = currentHour != null && i === currentHour;
    return {
      hour: `${String(i).padStart(2, '0')}:00`,
      actualPacKw: val,
      actual7Kw: val * 0.88,
      unit6Kw: val * 0.12,
      coverage: val > 0 ? 8 : 0,
      qualityFlag: val > 0 ? 'OK' : 'NODATA',
      readAtLocal: `${dateStr} ${String(i).padStart(2, '0')}:30`,
      samplePosition: isCurrent ? 'live_current' : 'H+30',
      status: isCurrent ? 'current' : 'past',
      isFinal: !isCurrent,
    };
  });
}

export function getMockPvActualDay(dateStr: string): PvActualDayResponse {
  const todayStr = fmtDateShort(today);
  const dateObj = new Date(dateStr);
  const diff = Math.round((dateObj.getTime() - new Date(todayStr).getTime()) / 86400000);

  let curve: number[];
  if (diff === -2) curve = dMinus2Actual;
  else if (diff === -1) curve = yesterdayActual;
  else if (diff === 0) curve = typicalActual;
  else if (diff === 1) curve = tomorrowActual;
  else curve = d2Actual;

  const isFutureDay = diff > 0;
  // Simulate current hour as ~14:00 UTC+3 for "today"
  const currentHour = diff === 0 ? 21 : undefined;

  const actual = buildPvActualData(dateStr, curve, isFutureDay, currentHour);
  const totalApprox = actual.reduce((s, h) => s + (h.actualPacKw ?? 0), 0);
  return {
    date: dateStr,
    totalActualKwhApprox: parseFloat(totalApprox.toFixed(1)),
    actual,
  };
}

// ─── Half-hour mock builders ─────────────────────────────────────────────────
function interpolate(a: number, b: number, t: number): number {
  return parseFloat((a + (b - a) * t).toFixed(3));
}

function buildHalfHourPoints(dateStr: string, forecastCurve: number[], actualCurve: number[], isFutureDay: boolean = false, currentHour?: number): ForecastHalfHourPoint[] {
  const clouds = cloudsByDay[dateStr] ?? Array(24).fill(20);
  const temps = tempsByDay[dateStr] ?? Array(24).fill(15);
  const wind = windByDay[dateStr] ?? Array(24).fill(3);
  const points: ForecastHalfHourPoint[] = [];

  for (let h = 0; h < 24; h++) {
    const fc0 = forecastCurve[h] ?? 0;
    const fc1 = forecastCurve[h + 1] ?? 0;
    const ac0 = actualCurve[h] ?? 0;
    const ac1 = actualCurve[h + 1] ?? 0;

    // Two half-hour slots per hour
    for (let slot = 0; slot < 2; slot++) {
      const t = slot === 0 ? 0.0 : 0.5;
      const minLabel = slot === 0 ? '00' : '30';
      const timeLabel = `${String(h).padStart(2, '0')}:${minLabel}`;
      const forecastSlot = `${String(h).padStart(2, '0')}:${minLabel}–${String(h).padStart(2, '0')}:${slot === 0 ? '30' : '00'} (next hour)`;

      const baseKw = interpolate(fc0, fc1, t);
      const baseKwh = parseFloat((baseKw * 0.5).toFixed(3));

      let actualKw: number | null = null;
      let actualKwh: number | null = null;
      if (!isFutureDay) {
        if (currentHour != null && h > currentHour) {
          // Use exact hourly actual if available — zero is a valid reading, do not drop it
          actualKw = ac0 ?? null;
        } else if (currentHour != null && h === currentHour && slot === 1) {
          // second half of current hour — use exact hourly value, not dampened interpolation
          actualKw = ac0 ?? null;
        } else {
          actualKw = interpolate(ac0, ac1, t);
        }
        if (actualKw !== null && actualKw !== undefined) {
          actualKwh = parseFloat((actualKw * 0.5).toFixed(3));
        }
      }

      const deltaBaseKw = actualKw != null ? actualKw - baseKw : null;
      const deltaBasePct = baseKw > 0 && actualKw != null ? ((actualKw - baseKw) / baseKw) * 100 : null;
      const baseAbsErrorKw = actualKw != null ? Math.abs(actualKw - baseKw) : null;

      points.push({
        time: timeLabel,
        forecastSlot,
        sampleTargetLocal: `${dateStr} ${timeLabel}`,
        baseForecastKw: baseKw,
        baseForecastKwh: baseKwh,
        actualPacKw: actualKw,
        actualKwhApprox: actualKwh,
        actualPvEquivalentKw: actualKw,
        deltaBaseKw,
        deltaBasePct,
        baseAbsErrorKw,
        bessChargeKw: actualKw != null && actualKw > baseKw ? (actualKw - baseKw) * 0.8 : null,
        bessDischargeKw: actualKw != null && actualKw < baseKw ? (baseKw - actualKw) * 0.6 : null,
        bessPowerKw: actualKw != null ? (actualKw - baseKw) * 0.7 : null,
        socPct: h >= 6 && h <= 18 ? 55 + Math.round((h - 6 + slot * 0.5) * 1.5) : 30,
        calibrationMode: h >= 6 && h <= 18 ? (slot === 0 ? 'daylight_first_half' : 'daylight_second_half') : 'night',
        calibrationWeight: 0.85,
        shortwaveRadiation: Math.round((baseKw / Math.max(4.5, 0.01)) * 920),
        directRadiation: Math.round((baseKw / Math.max(4.5, 0.01)) * 920 * 0.55),
        diffuseRadiation: Math.round((baseKw / Math.max(4.5, 0.01)) * 920 * 0.35),
        cloudCover: clouds[h] ?? 20,
        temperature2m: temps[h] ?? 15,
        weatherInterpolation: slot === 0 ? 'native_hour' : 'linear_30min',
        coverage: actualKw != null ? 8 : 0,
        qualityFlag: actualKw != null ? 'OK' : 'NODATA',
        bessQualityFlag: actualKw != null ? 'OK' : 'NODATA',
        deltaSeverity: actualKw != null ? (Math.abs(deltaBasePct ?? 0) > 20 ? 'high' : Math.abs(deltaBasePct ?? 0) > 10 ? 'medium' : 'low') : null,
      });
    }
  }

  return points;
}

function makeHalfHourSummary(points: ForecastHalfHourPoint[]): ForecastHalfHourSummary {
  const forecastTotal = points.reduce((s, p) => s + (p.baseForecastKwh ?? 0), 0);
  const actualTotal = points.reduce((s, p) => s + (p.actualKwhApprox ?? 0), 0);
  const absErr = points.reduce((s, p) => {
    if (p.actualKwhApprox == null || p.baseForecastKwh == null) return s;
    return s + Math.abs(p.actualKwhApprox - p.baseForecastKwh);
  }, 0);
  const rowsWithFc = points.filter((p) => p.baseForecastKw != null).length;
  const rowsWithAct = points.filter((p) => p.actualPacKw !== null && p.actualPacKw !== undefined).length;
  const hasActual = rowsWithAct > 0;

  return {
    forecastKwh: parseFloat(forecastTotal.toFixed(1)),
    actualKwh: hasActual ? parseFloat(actualTotal.toFixed(1)) : null,
    absErrorKwh: hasActual ? parseFloat(absErr.toFixed(1)) : null,
    rowsWithForecast: rowsWithFc,
    rowsWithActual: rowsWithAct,
    interval: '30min',
    actualKwhApprox: hasActual ? parseFloat(actualTotal.toFixed(3)) : null,
    baseForecastKwhApprox: parseFloat(forecastTotal.toFixed(3)),
  };
}

export function getMockForecastHalfHour(dateStr: string): ForecastHalfHourResponse {
  const todayStr = fmtDateShort(today);
  const dateObj = new Date(dateStr);
  const diff = Math.round((dateObj.getTime() - new Date(todayStr).getTime()) / 86400000);

  let forecastCurve: number[];
  let actualCurve: number[];
  if (diff === -2) { forecastCurve = dMinus2Forecast; actualCurve = dMinus2Actual; }
  else if (diff === -1) { forecastCurve = yesterdayForecast; actualCurve = yesterdayActual; }
  else if (diff === 0) { forecastCurve = typicalForecast; actualCurve = typicalActual; }
  else if (diff === 1) { forecastCurve = tomorrowForecast; actualCurve = tomorrowActual; }
  else { forecastCurve = d2Forecast; actualCurve = d2Actual; }

  const isFutureDay = diff > 0;
  const currentHour = diff === 0 ? 21 : undefined;

  const points = buildHalfHourPoints(dateStr, forecastCurve, actualCurve, isFutureDay, currentHour);
  const summary = makeHalfHourSummary(points);

  return {
    date: dateStr,
    interval: '30min',
    rows: points.length,
    summary,
    points,
  };
}

// ─── Forecast Comparison mock data ────────────────────────────────────────────
function buildShadowForecast(base: number[]): number[] {
  // Shadow v0.3.4 is slightly different from base forecast
  return base.map((v) => {
    if (v === 0) return 0;
    const jitter = (Math.random() - 0.5) * 0.15;
    return parseFloat(Math.max(0, v + jitter).toFixed(3));
  });
}

function buildShadowForecastV038(base: number[]): number[] {
  // ECMWF adjusted shadow v0.3.8: uses ECMWF weather comparison and BESS-adjusted calibration
  return base.map((v, i) => {
    if (v === 0) return 0;
    // v0.3.8 is typically slightly more accurate than v0.3.4
    const weatherCorrection = Math.sin((i / 24) * Math.PI) * 0.08;
    const jitter = (Math.random() - 0.5) * 0.08;
    return parseFloat(Math.max(0, v + weatherCorrection + jitter).toFixed(3));
  });
}

function buildComparisonPoints(
  dateStr: string,
  forecastCurve: number[],
  actualCurve: number[],
  isFutureDay: boolean = false,
  currentHour?: number,
): ForecastComparisonPoint[] {
  const shadowCurve = buildShadowForecast(forecastCurve);
  const shadowV038Curve = buildShadowForecastV038(forecastCurve);
  const clouds = cloudsByDay[dateStr] ?? Array(24).fill(20);

  const weatherSignals = [
    'ecmwf_similar',
    'ecmwf_significantly_more_sun',
    'ecmwf_slightly_less_sun',
    'ecmwf_significantly_more_sun',
    'ecmwf_similar',
  ];

  return forecastCurve.map((base, i) => {
    const hour = `${String(i).padStart(2, '0')}:00`;
    const shadow = shadowCurve[i];
    const shadowV038 = shadowV038Curve[i];
    let actual: number | null = null;

    if (!isFutureDay) {
      actual = actualCurve[i];
    }

    const deltaBaseKw = actual != null && base != null ? actual - base : null;
    const deltaBasePct = base != null && base > 0 && actual != null ? ((actual - base) / base) * 100 : null;
    const deltaV034Kw = actual != null && shadow != null ? actual - shadow : null;
    const deltaV034Pct = shadow != null && shadow > 0 && actual != null ? ((actual - shadow) / shadow) * 100 : null;
    const deltaV038Kw = actual != null && shadowV038 != null ? actual - shadowV038 : null;
    const deltaV038Pct = shadowV038 != null && shadowV038 > 0 && actual != null ? ((actual - shadowV038) / shadowV038) * 100 : null;
    const v038AbsErrorKw = actual != null && shadowV038 != null ? Math.abs(actual - shadowV038) : null;

    return {
      hour,
      forecastHour: null,
      baseForecastKw: base,
      actualPacKw: actual,
      v034ShadowForecastKw: shadow,
      v038ShadowForecastKw: shadowV038,
      deltaBaseKw,
      deltaBasePct,
      deltaV034Kw,
      deltaV034Pct,
      deltaV038Kw,
      deltaV038Pct,
      v038AbsErrorKw,
      calibrationMode: i >= 6 && i <= 18 ? 'daylight' : 'night',
      bessChargeKw: actual != null && actual > base ? (actual - base) * 0.8 : null,
      bessDischargeKw: actual != null && actual < base ? (base - actual) * 0.6 : null,
      socPct: i >= 6 && i <= 18 ? 65 + Math.round((i - 6) * 1.5) : 30,
      cloudCover: clouds[i] ?? 20,
      shortwaveRadiation: Math.round((base / Math.max(4.5, 0.01)) * 920),
      directRadiation: Math.round((base / Math.max(4.5, 0.01)) * 920 * 0.55),
      ecmwfShortwaveRadiation: Math.round((shadowV038 / Math.max(4.5, 0.01)) * 880),
      ecmwfDirectRadiation: Math.round((shadowV038 / Math.max(4.5, 0.01)) * 880 * 0.52),
      weatherComparisonSignal: weatherSignals[i % weatherSignals.length],
      v034CorrectionReason: Math.abs((shadow - base) / Math.max(base, 0.01)) > 0.05 ? 'weather_adjustment' : null,
      v038CorrectionReason: Math.abs((shadowV038 - base) / Math.max(base, 0.01)) > 0.05 ? 'v038_post_bess_recovery_confirmed_ecmwf_uplift' : null,
    };
  });
}

function makeComparisonSummary(points: ForecastComparisonPoint[]): ForecastComparisonSummary {
  const baseTotal = points.reduce((s, p) => s + (p.baseForecastKw ?? 0), 0);
  const actualTotal = points.reduce((s, p) => s + (p.actualPacKw ?? 0), 0);
  const shadowTotal = points.reduce((s, p) => s + (p.v034ShadowForecastKw ?? 0), 0);
  const shadowV038Total = points.reduce((s, p) => s + (p.v038ShadowForecastKw ?? 0), 0);
  const baseAbsErr = points.reduce((s, p) => {
    if (p.actualPacKw == null || p.baseForecastKw == null) return s;
    return s + Math.abs(p.actualPacKw - p.baseForecastKw);
  }, 0);
  const v034AbsErr = points.reduce((s, p) => {
    if (p.actualPacKw == null || p.v034ShadowForecastKw == null) return s;
    return s + Math.abs(p.actualPacKw - p.v034ShadowForecastKw);
  }, 0);
  const v038AbsErr = points.reduce((s, p) => {
    if (p.actualPacKw == null || p.v038ShadowForecastKw == null) return s;
    return s + Math.abs(p.actualPacKw - p.v038ShadowForecastKw);
  }, 0);
  const improvement = baseAbsErr - v034AbsErr;
  const improvementV038 = baseAbsErr - v038AbsErr;
  const improvementV038vsV034 = v034AbsErr - v038AbsErr;

  const hasActual = points.some((p) => p.actualPacKw !== null && p.actualPacKw !== undefined);

  return {
    baseForecastKwh: parseFloat(baseTotal.toFixed(1)),
    actualKwh: hasActual ? parseFloat(actualTotal.toFixed(1)) : null,
    v034ShadowKwh: parseFloat(shadowTotal.toFixed(1)),
    v038ShadowKwh: parseFloat(shadowV038Total.toFixed(1)),
    baseAbsErrorKwh: hasActual ? parseFloat(baseAbsErr.toFixed(1)) : null,
    v034AbsErrorKwh: hasActual ? parseFloat(v034AbsErr.toFixed(1)) : null,
    v038AbsErrorKwh: hasActual ? parseFloat(v038AbsErr.toFixed(1)) : null,
    improvementVsBaseKwh: hasActual ? parseFloat(improvement.toFixed(1)) : null,
    improvementV038VsBaseKwh: hasActual ? parseFloat(improvementV038.toFixed(1)) : null,
    v038ShadowKwhApprox: parseFloat(shadowV038Total.toFixed(1)),
    v038AbsErrorKwhApprox: hasActual ? parseFloat(v038AbsErr.toFixed(1)) : null,
    v038ImprovementVsBaseKwhApprox: hasActual ? parseFloat(improvementV038.toFixed(1)) : null,
    v038ImprovementVsV034KwhApprox: hasActual ? parseFloat(improvementV038vsV034.toFixed(1)) : null,
  };
}

export function getMockForecastComparison(dateStr: string): ForecastComparisonResponse {
  const todayStr = fmtDateShort(today);
  const dateObj = new Date(dateStr);
  const diff = Math.round((dateObj.getTime() - new Date(todayStr).getTime()) / 86400000);

  let forecastCurve: number[];
  let actualCurve: number[];
  if (diff === -2) { forecastCurve = dMinus2Forecast; actualCurve = dMinus2Actual; }
  else if (diff === -1) { forecastCurve = yesterdayForecast; actualCurve = yesterdayActual; }
  else if (diff === 0) { forecastCurve = typicalForecast; actualCurve = typicalActual; }
  else if (diff === 1) { forecastCurve = tomorrowForecast; actualCurve = tomorrowActual; }
  else { forecastCurve = d2Forecast; actualCurve = d2Actual; }

  const isFutureDay = diff > 0;
  const currentHour = diff === 0 ? 21 : undefined;

  const points = buildComparisonPoints(dateStr, forecastCurve, actualCurve, isFutureDay, currentHour);
  const summary = makeComparisonSummary(points);

  return {
    date: dateStr,
    assetId: ASSET_ID,
    points,
    summary,
  };
}

// ─── Build day response ───────────────────────────────────────────────────────
export function getMockDayForecast(dateStr: string): GenerationForecastDayResponse {
  const todayStr = fmtDateShort(today);
  const dateObj = new Date(dateStr);
  const diff = Math.round((dateObj.getTime() - new Date(todayStr).getTime()) / 86400000);

  let curve: number[];
  if (diff === -2) curve = dMinus2Forecast;
  else if (diff === -1) curve = yesterdayForecast;
  else if (diff === 0) curve = typicalForecast;
  else if (diff === 1) curve = tomorrowForecast;
  else curve = d2Forecast;

  const hourly = buildHourlyData(dateStr, curve);
  return {
    date: dateStr,
    modelVersion: 'v0.2.1-dc-temp-clipped-daylight-openmeteo-forecast',
    targetType: 'sale_export',
    inputWeatherProvider: 'open-meteo',
    inputWeatherRunAt: `${dateStr}T02:00:00Z`,
    forecastRunAt: `${todayStr}T03:00:00Z`,
    summary: makeDaySummary(hourly),
    hourly,
    sun: makeSun(dateStr),
  };
}

// ─── Build latest summary response ────────────────────────────────────────────
const allDaily: GenerationForecastDaily[] = getDayTabLabels().map((tab) => {
  const dayData = getMockDayForecast(tab.apiDate);
  return {
    day: tab.apiDate,
    rows: dayData.summary.rows,
    missingRows: dayData.summary.missingRows,
    forecastEnergyKwh: dayData.summary.forecastEnergyKwh,
    maxHourlyKwh: dayData.summary.maxHourlyKwh,
    avgCloudCover: dayData.summary.avgCloudCover,
    maxShortwaveRadiation: dayData.summary.maxShortwaveRadiation,
    minTemperature2m: dayData.summary.minTemperature2m,
    maxTemperature2m: dayData.summary.maxTemperature2m,
  };
});

export const mockGenerationForecastLatest: GenerationForecastLatestResponse = {
  modelVersion: 'v0.2.1-dc-temp-clipped-daylight-openmeteo-forecast',
  forecastRunAt: `${fmtDateShort(today)}T03:00:00Z`,
  forecastWindow: {
    fromTimestamp: `${fmtDateShort(shiftDate(today, 0))}T00:00:00Z`,
    toTimestamp: `${fmtDateShort(shiftDate(today, 2))}T23:00:00Z`,
    fromLocal: `${formatDateLocal(shiftDate(today, 0))} 00:00`,
    toLocal: `${formatDateLocal(shiftDate(today, 2))} 23:00`,
    fromDate: fmtDateShort(shiftDate(today, 0)),
    toDate: fmtDateShort(shiftDate(today, 2)),
  },
  summary: {
    rows: allDaily.reduce((s, d) => s + d.rows, 0),
    totalForecastEnergyKwh: parseFloat(allDaily.reduce((s, d) => s + d.forecastEnergyKwh, 0).toFixed(1)),
    maxHourlyKwh: Math.max(...allDaily.map((d) => d.maxHourlyKwh)),
    firstForecastForLocal: `${formatDateLocal(shiftDate(today, 0))} 00:00`,
    lastForecastForLocal: `${formatDateLocal(shiftDate(today, 2))} 23:00`,
  },
  daily: allDaily,
};

// ─── Helpers for components ────────────────────────────────────────────────────
export function fmtKwh(v: number | null | undefined, digits = 1): string {
  if (v == null) return '—';
  return Number.isInteger(v) ? `${v}` : v.toFixed(digits);
}

export function fmtInt(v: number | null | undefined): string {
  if (v == null) return '—';
  return Math.round(v).toString();
}

export function fmtDec(v: number | null | undefined, digits = 1): string {
  if (v == null) return '—';
  return v.toFixed(digits);
}

export function fmtDateLocal(ts: string | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function fmtDateTimeLocal(ts: string | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString('uk-UA', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtTimeHHmm(ts: string | undefined): string {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
}

const ASSET_ID = '12a78662-a28d-40d5-9eb7-e83ebb158b41';